import { useCallback, useEffect, useRef, useState } from 'react';

import { dispatchSnackbarWarningEvent } from '@dreryk/react-components';

import { ElevenLabsTranscriptionConfig } from '../transcription/ElevenLabsTranscriptionConfig';
import { useElevenLabsTranscription } from '../transcription/useElevenLabsTranscription';
import { useRedaction } from '../transcription/useRedaction';
import { useVisitTranscriptSummary } from '../transcription/useVisitTranscriptSummary';
import { buildSummaryUtterances, updateUtterances } from '../transcription/utterances';
import { translationKeys } from '../translations';
import type { RedactionScope, Utterance, VisitTranscriptSummaryResult } from '../types';

export type UseContinuousPanelResult = {
   transcription: ReturnType<typeof useElevenLabsTranscription>;
   redaction: ReturnType<typeof useRedaction>;
   utterances: Utterance[];
   continuousText: string;
   summaryLoading: boolean;
   summaryResult: VisitTranscriptSummaryResult | null;
   showProposalModal: boolean;
   consentOpen: boolean;
   isRecording: boolean;
   isConnecting: boolean;
   hasContinuousText: boolean;
   silenceSecondsRemaining: number | null;
   /** Additive total recording time in seconds across all start/stop cycles. */
   totalRecordingSeconds: number;
   handleRecordClick: VoidFunction;
   handleConsentConfirm: VoidFunction;
   handleConsentCancel: VoidFunction;
   handleSummarize: VoidFunction;
   handleCloseProposalModal: VoidFunction;
};

/*
 * Continuous mode routes all transcript through onSpeakerTranscript.
 * The plain onPartial / onFinal callbacks are intentionally no-ops.
 */
function noopCallback(): void {
   // Intentional no-op for plain partial/final callbacks in continuous mode
}

// eslint-disable-next-line max-lines-per-function,max-statements
export const useContinuousPanel = (
   config: ElevenLabsTranscriptionConfig,
   onRecordingChange: (isRecording: boolean) => void
): UseContinuousPanelResult => {
   const [utterances, setUtterances] = useState<Utterance[]>([]);
   const [continuousText, setContinuousText] = useState('');
   const continuousTextRef = useRef(continuousText);
   continuousTextRef.current = continuousText;

   /*
    * Tracks the last utterance if it is currently partial, null otherwise.
    * Updated synchronously alongside setUtterances so handleStop can read it
    * without closing over stale state or nesting setState calls.
    */
   const lastPartialUtteranceRef = useRef<Utterance | null>(null);

   const [summaryLoading, setSummaryLoading] = useState(false);
   const [summaryResult, setSummaryResult] = useState<VisitTranscriptSummaryResult | null>(null);
   const [showProposalModal, setShowProposalModal] = useState(false);
   const [consentOpen, setConsentOpen] = useState(false);
   const [silenceSecondsRemaining, setSilenceSecondsRemaining] = useState<number | null>(null);

   /*
    * Additive recording timer.
    * accumulatedSecondsRef holds the total from all completed recording sessions.
    * sessionStartRef holds the Date.now() of the current session start, or null when stopped.
    * totalRecordingSeconds is the rendered value — updated by a 1s interval while recording.
    */
   const [totalRecordingSeconds, setTotalRecordingSeconds] = useState(0);
   const accumulatedSecondsRef = useRef(0);
   const sessionStartRef = useRef<number | null>(null);
   const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

   const handleSpeakerTranscript = useCallback((speaker: string, text: string, isPartial: boolean) => {
      lastPartialUtteranceRef.current = isPartial ? { speaker: speaker, text: text, isPartial: isPartial } : null;
      setUtterances((prev) => updateUtterances(prev, speaker, text, isPartial));
      if (!isPartial) {
         setContinuousText((prev) => `${prev + text} `);
      }
   }, []);

   const handleStop = useCallback(() => {
      setSilenceSecondsRemaining(null);
      // Read via ref — always current, no stale closure, no nested setState.
      const pending = lastPartialUtteranceRef.current;
      lastPartialUtteranceRef.current = null;
      if (!pending) {
         return;
      }
      // Promote the last partial utterance to final
      setUtterances((prev) => {
         const last = prev[prev.length - 1];
         if (!last || !last.isPartial) {
            return prev;
         }
         const updated = [...prev];
         updated[updated.length - 1] = { ...last, isPartial: false };
         return updated;
      });
      setContinuousText((prev) => `${prev + pending.text} `);
   }, []);

   const transcriptionOptions = {
      onPartial: noopCallback,
      onFinal: noopCallback,
      onSpeakerTranscript: handleSpeakerTranscript,
      onStop: handleStop,
      onSilenceSeconds: useCallback((s: number) => setSilenceSecondsRemaining(s), []),
      onSpeaking: useCallback(() => setSilenceSecondsRemaining(null), []),
      onSilenceDisconnect: useCallback(() => {
         setSilenceSecondsRemaining(null);
         dispatchSnackbarWarningEvent(translationKeys.faults.microphoneSilenceDisconnect);
      }, []),
   };

   const transcription = useElevenLabsTranscription(transcriptionOptions);

   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   const handleRedactionAccept = useCallback((redactedText: string, _scope: RedactionScope) => {
      setContinuousText(redactedText);
   }, []);

   const getFieldText = useCallback(() => continuousTextRef.current, []);
   const getSelectionRange = useCallback(() => null, []);

   const redaction = useRedaction({
      field: () => 'INTERVIEW',
      getFieldText: getFieldText,
      getSelectionRange: getSelectionRange,
      onAccept: handleRedactionAccept,
   });

   const summaryCommand = useVisitTranscriptSummary({
      onSuccess: (result) => {
         setSummaryLoading(false);
         setSummaryResult(result);
         setShowProposalModal(true);
      },
   });

   useEffect(() => {
      if (summaryCommand.error && summaryLoading) {
         setSummaryLoading(false);
      }
   }, [summaryCommand.error, summaryLoading]);

   const isRecording = transcription.state === 'recording';
   const isConnecting = transcription.state === 'requesting-credentials' || transcription.state === 'connecting';
   const hasContinuousText = continuousText.trim().length > 0;

   /*
    * Recording timer — starts when recording begins, stops and accumulates when it ends.
    * A 1s interval updates totalRecordingSeconds while active.
    */
   useEffect(() => {
      if (isRecording) {
         sessionStartRef.current = Date.now();
         timerIntervalRef.current = setInterval(() => {
            const elapsed =
               // eslint-disable-next-line no-negated-condition
               sessionStartRef.current !== null ? Math.floor((Date.now() - sessionStartRef.current) / 1000) : 0;
            setTotalRecordingSeconds(accumulatedSecondsRef.current + elapsed);
         }, 1000);
      } else {
         if (timerIntervalRef.current !== null) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
         }
         if (sessionStartRef.current !== null) {
            accumulatedSecondsRef.current += Math.floor((Date.now() - sessionStartRef.current) / 1000);
            sessionStartRef.current = null;
            setTotalRecordingSeconds(accumulatedSecondsRef.current);
         }
      }
      return () => {
         if (timerIntervalRef.current !== null) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
         }
      };
   }, [isRecording]);

   /*
    * Drive onRecordingChange from state, not from imperative call sites.
    * This is the single source of truth — fires even when the session ends
    * due to a WS timeout or unmount, not just on user-click.
    */
   const onRecordingChangeRef = useRef(onRecordingChange);
   onRecordingChangeRef.current = onRecordingChange;

   useEffect(() => {
      onRecordingChangeRef.current(isRecording);
   }, [isRecording]);

   const handleRecordClick = useCallback(() => {
      if (isRecording) {
         transcription.stop();
      } else {
         setConsentOpen(true);
      }
   }, [isRecording, transcription]);

   const handleConsentConfirm = useCallback(() => {
      setConsentOpen(false);
      // eslint-disable-next-line no-void
      void transcription.start(true, config);
   }, [transcription, config]);

   const handleConsentCancel = useCallback(() => {
      setConsentOpen(false);
      dispatchSnackbarWarningEvent(translationKeys.continuousMode.consentRequired);
   }, []);

   const handleSummarize = useCallback(() => {
      const summaryUtterances = buildSummaryUtterances(utterances);
      if (summaryUtterances.length === 0) {
         return;
      }
      setSummaryLoading(true);
      summaryCommand.execute({ utterances: summaryUtterances });
   }, [utterances, summaryCommand]);

   const handleCloseProposalModal = useCallback(() => {
      setShowProposalModal(false);
   }, []);

   return {
      transcription: transcription,
      redaction: redaction,
      utterances: utterances,
      continuousText: continuousText,
      summaryLoading: summaryLoading,
      summaryResult: summaryResult,
      showProposalModal: showProposalModal,
      consentOpen: consentOpen,
      isRecording: isRecording,
      isConnecting: isConnecting,
      hasContinuousText: hasContinuousText,
      silenceSecondsRemaining: silenceSecondsRemaining,
      totalRecordingSeconds: totalRecordingSeconds,
      handleRecordClick: handleRecordClick,
      handleConsentConfirm: handleConsentConfirm,
      handleConsentCancel: handleConsentCancel,
      handleSummarize: handleSummarize,
      handleCloseProposalModal: handleCloseProposalModal,
   };
};
